import { FC, useCallback, useEffect, useState } from 'react';
import { Chip, CircularProgress, Divider, Grid, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, tableCellClasses } from '@mui/material';
import styled_ from '@emotion/styled';
import moment from 'moment';

import { useAppDispatch } from '../../store/hooks';
import { fetchCashInflowPaymentsDetails } from '../../store/CashInflowStore/cashInflowThunk';
import { fetchCashOutflowPaymentsDetails } from '../../store/CashOutflowStore/cashOutflowThunk';
import { Utils } from '../../utils/common-utils';

const FETCH_BATCH = 100;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const StyledTableCell = styled_(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#9da7b7',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: 13,
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: 5,
    paddingLeft: '15px !important',
    borderBottom: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StyledTableRow = styled_(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: '#e9ecf1' },
  '&:nth-of-type(even)': { backgroundColor: '#fbfbfb' },
}));

interface PaymentRow {
  id: string;
  txnId: string;
  type: 'RECEIVED' | 'PAID';
  date: number;
  amount: number;
  mode: string;
  bankName: string;
  status: string;
  invoiceNos: string[];
}

interface PartyPaymentHistoryProps {
  partyId: string;
  isClient?: boolean;
  isVendor?: boolean;
}

const toRow = (d: any, type: 'RECEIVED' | 'PAID'): PaymentRow => ({
  id: d._id,
  txnId: `${type === 'RECEIVED' ? 'PR' : 'PO'}-${d.txnId ?? ''}`,
  type,
  date: d.paymentDate,
  amount: parseFloat(d.amount) || 0,
  mode: d.paymentMode || '-',
  bankName: (type === 'RECEIVED' ? d.fromBankAccount?.bankName : d.toBankAccount?.bankName) || '',
  status: d.applyStatus || '',
  invoiceNos: (d.invoices || [])
    .map((i: any) => i.invoice?.invoiceNumber)
    .filter((n: any): n is string => Boolean(n)),
});

// Same labels/colours as the invoice list (Paid = green, Partially Paid = blue, Pending = red)
const statusStyle = (status: string) => {
  if (status === 'FULLY_APPLIED' || status === 'APPLIED') return { color: '#49a563', backgroundColor: '#edf6ef' };
  if (status === 'PARTIALLY_APPLIED') return { color: '#488dff', backgroundColor: '#f1f0fc' };
  return { color: '#c33b3b', backgroundColor: '#fbecec' }; // UNAPPLIED
};
const statusLabel = (status: string) => {
  if (status === 'FULLY_APPLIED' || status === 'APPLIED') return 'Paid';
  if (status === 'PARTIALLY_APPLIED') return 'Partially Paid';
  return 'Pending';
};

const Row: FC<{ row: PaymentRow }> = ({ row }) => {
  return (
    <StyledTableRow>
      <StyledTableCell>{row.txnId}</StyledTableCell>
      <StyledTableCell>{row.date ? moment(row.date).format('DD MMM YYYY') : '-'}</StyledTableCell>
      <StyledTableCell title={row.invoiceNos.join(', ')}>{row.invoiceNos.length > 0 ? row.invoiceNos.join(', ') : '-'}</StyledTableCell>
      <StyledTableCell>{row.mode}{row.bankName ? ` (${row.bankName})` : ''}</StyledTableCell>
      <StyledTableCell style={{ fontWeight: 600 }}>{Utils.formatNumber(row.amount, true, true)}</StyledTableCell>
      <StyledTableCell style={{ paddingRight: 15 }}>
        {row.status ? (
          <Chip
            size="small"
            label={statusLabel(row.status)}
            style={{ borderRadius: 7, ...statusStyle(row.status) }}
          />
        ) : '-'}
      </StyledTableCell>
    </StyledTableRow>
  );
};

// Payment History for a party: receipts from a customer (inflow) and payments to a vendor (outflow),
// fetched through the existing payment-application APIs filtered by buyer/supplier.
const PartyPaymentHistory: FC<PartyPaymentHistoryProps> = ({ partyId, isClient, isVendor }) => {
  const dispatch = useAppDispatch();

  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  // Party with neither flag (e.g. agent only) — try both so nothing is silently hidden
  const fetchInflow = isClient || (!isClient && !isVendor);
  const fetchOutflow = isVendor || (!isClient && !isVendor);

  // Pull every page of one source (receipts or vendor payments) for this party
  const fetchAll = useCallback(async (thunk: any): Promise<any[]> => {
    const all: any[] = [];
    let offset = 0;
    let hasMore = true;
    while (hasMore) {
      const res: any = await dispatch(thunk({ selectedBuyer: partyId, limit: FETCH_BATCH, offset }));
      const batch = res?.payload?.payments || (Array.isArray(res?.payload) ? res.payload : []);
      all.push(...batch);
      hasMore = Boolean(res?.payload?.hasMore) && batch.length > 0;
      offset += FETCH_BATCH;
    }
    return all;
  }, [partyId, dispatch]);

  useEffect(() => {
    if (!partyId) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const [inflow, outflow] = await Promise.all([
          fetchInflow ? fetchAll(fetchCashInflowPaymentsDetails) : Promise.resolve([]),
          fetchOutflow ? fetchAll(fetchCashOutflowPaymentsDetails) : Promise.resolve([]),
        ]);
        if (cancelled) return;
        const merged: PaymentRow[] = [
          ...inflow.map((d: any) => toRow(d, 'RECEIVED')),
          ...outflow.map((d: any) => toRow(d, 'PAID')),
        ].sort((a, b) => (b.date || 0) - (a.date || 0));
        setRows(merged);
        setPage(0);
      } catch (error) {
        console.error('Error fetching party payment history:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [partyId, fetchInflow, fetchOutflow, fetchAll]);

  const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div className="c-card2" style={{ padding: 0 }}>
          <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginLeft: 15, marginTop: 7 }}>Payment History</h5>

          {isLoading ? (
            <div style={{ height: 200, textAlign: 'center', paddingTop: 60 }}>
              <CircularProgress size={28} color="info" />
            </div>
          ) : rows.length > 0 ? (
            <div style={{ marginBottom: 12, marginTop: 5, overflowX: 'auto' }}>
              <Table stickyHeader sx={{ width: '100%', tableLayout: 'fixed', minWidth: 900 }} size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell width="16.66%">Txn No</StyledTableCell>
                    <StyledTableCell width="16.66%">Date</StyledTableCell>
                    <StyledTableCell width="16.66%">Invoice #</StyledTableCell>
                    <StyledTableCell width="16.66%">Mode</StyledTableCell>
                    <StyledTableCell width="16.66%">Amount</StyledTableCell>
                    <StyledTableCell width="16.66%" style={{ paddingRight: 15 }}>Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map(row => <Row key={`${row.type}-${row.id}`} row={row} />)}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Rows per page"
                sx={{
                  borderTop: '0.25px solid #d9d4d4',
                  backgroundColor: '#fff',
                  '& .MuiTablePagination-toolbar': { minHeight: 44, paddingLeft: 2, paddingRight: 1 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: 13, color: '#545454', margin: 0,
                  },
                  '& .MuiTablePagination-select': { fontSize: 13, color: '#292d35', paddingTop: '6px', paddingBottom: '6px' },
                  '& .MuiTablePagination-actions .MuiIconButton-root': {
                    color: '#488dff',
                    padding: '6px',
                    '&.Mui-disabled': { color: '#cbd5e1' },
                  },
                }}
              />
            </div>
          ) : (
            <div style={{ height: 200 }}>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
              <span style={{ marginLeft: 15 }}>No payments recorded for this party</span>
            </div>
          )}
        </div>
      </Grid>
    </Grid>
  );
};

export default PartyPaymentHistory;
